/**
 * Campaign Views Constants
 * 
 * View types, labels, icons, and default settings for custom campaign views.
 */

// ═══════════════════════════════════════════════════════════════
// VIEW TYPES
// ═══════════════════════════════════════════════════════════════

/**
 * Available view types that can be created
 */
export const VIEW_TYPES = {
  FLOW: 'flow',
  KANBAN: 'kanban',
  TABLE: 'table',
  GANTT: 'gantt',
  TODOLIST: 'todolist',
  NEURAL: 'neural',
  BOARD: 'board',
};

/**
 * Display names for view types
 */
export const VIEW_TYPE_LABELS = {
  [VIEW_TYPES.FLOW]: 'Flow',
  [VIEW_TYPES.KANBAN]: 'Kanban',
  [VIEW_TYPES.TABLE]: 'Table',
  [VIEW_TYPES.GANTT]: 'Gantt',
  [VIEW_TYPES.TODOLIST]: 'Todo',
  [VIEW_TYPES.NEURAL]: 'Astra',
  [VIEW_TYPES.BOARD]: 'Board',
};

const STOCK_NEURAL_NAMES = new Set(['Neural', 'Astro']);

/** Stock neural tabs used to be Neural, then Astro. Custom names stay. */
export function relabelStockViewNames(views) {
  if (!Array.isArray(views)) return views;
  let changed = false;
  const next = views.map((view) => {
    if (view?.type === VIEW_TYPES.NEURAL && STOCK_NEURAL_NAMES.has(view.name)) {
      changed = true;
      return { ...view, name: VIEW_TYPE_LABELS[VIEW_TYPES.NEURAL] };
    }
    return view;
  });
  return changed ? next : views;
}

/**
 * Icons for view types (SVG paths)
 */
export const VIEW_TYPE_ICONS = {
  [VIEW_TYPES.FLOW]: {
    paths: [
      'M3 3h6v6H3zM15 3h6v6h-6zM9 15h6v6H9z',
      'M6 9v3h3M18 9v6h-3M12 9v6',
    ],
  },
  [VIEW_TYPES.KANBAN]: {
    rects: [
      { x: 3, y: 3, width: 5, height: 18, rx: 1 },
      { x: 10, y: 3, width: 5, height: 12, rx: 1 },
      { x: 17, y: 3, width: 5, height: 8, rx: 1 },
    ],
  },
  [VIEW_TYPES.TABLE]: {
    rects: [{ x: 3, y: 3, width: 18, height: 18, rx: 2 }],
    lines: [
      { x1: 3, y1: 9, x2: 21, y2: 9 },
      { x1: 3, y1: 15, x2: 21, y2: 15 },
      { x1: 9, y1: 3, x2: 9, y2: 21 },
    ],
  },
  [VIEW_TYPES.GANTT]: {
    rects: [
      { x: 3, y: 5, width: 18, height: 2, rx: 1 },
      { x: 3, y: 11, width: 12, height: 2, rx: 1 },
      { x: 3, y: 17, width: 15, height: 2, rx: 1 },
    ],
  },
  [VIEW_TYPES.TODOLIST]: {
    paths: [
      'M9 11l3 3L22 4',
      'M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11',
    ],
  },
  [VIEW_TYPES.NEURAL]: {
    paths: [
      'M7.5 8.5L12 12M12 12l4.8-4.2M12 12l-.2 5.8',
      'M7.5 8.5a2 2 0 114 0 2 2 0 01-4 0',
      'M15.8 16.8a2 2 0 11-4 0 2 2 0 014 0',
      'M17.8 9.8a2 2 0 11-4 0 2 2 0 014 0',
    ],
  },
  [VIEW_TYPES.BOARD]: {
    paths: [
      'M4 5h16v14H4z',
      'M8 9h5M8 13h8',
    ],
  },
};

// ═══════════════════════════════════════════════════════════════
// DEFAULT VIEW SETTINGS
// ═══════════════════════════════════════════════════════════════

/**
 * Default settings for each view type
 * 
 * Each view type has its own unique settings that are persisted per custom view:
 * 
 * FLOW:
 * - viewport (pan/zoom position)
 * - filters
 * - fokusMode (focused task IDs)
 * - flowViewVisibility (HUD, minimap, progress)
 * Note: Status limits are CAMPAIGN-WIDE, not per-view
 * Note: Show/hide field choices are added in createCustomView from the
 * field-visibility defaults. They are omitted here so a view that never
 * saved them is not restored to the old stub keys.
 * 
 * KANBAN:
 * - swimlaneSetting (swimlane mode)
 * - sortSetting
 * - fokusMode
 * - filters
 * 
 * TABLE:
 * - fokusMode
 * - sortSetting
 * - filters
 * 
 * GANTT:
 * - fokusMode
 * - viewport (zoom level, scroll position - always centers on NOW line)
 * - filters
 * Note: Status filter removed (redundant with filters)
 * 
 * TODO:
 * - fokusMode
 * - viewMode (master/simple/swimlane)
 * - swimlaneGroupBy (when in swimlane mode)
 * - sortSetting
 * - filters
 */
export const DEFAULT_VIEW_SETTINGS = {
  [VIEW_TYPES.FLOW]: {
    // Viewport state (pan/zoom)
    viewport: {
      pan: { x: 0, y: 0 },
      zoom: 1,
    },
    // Filters
    filters: {},
    // FOKUS mode
    fokusMode: {
      isActive: false,
      focusedTaskIds: [],
    },
    // Visibility settings (HUD elements)
    flowViewVisibility: {
      activeMissions: true,
      availableMissions: true,
      milestones: true,
      minimap: true,
      campaignsKey: true,
      // Collapsed states for mission lists
      activeMissionsCollapsed: false,
      availableMissionsCollapsed: false,
      milestonesCollapsed: false,
      // Minimap size
      minimapSize: { maxWidth: 200, maxHeight: 150 },
    },
  },
  [VIEW_TYPES.KANBAN]: {
    // Swimlane mode
    swimlaneSetting: 'none',
    // Sort
    sortSetting: 'manual',
    // FOKUS mode
    fokusMode: {
      isActive: false,
      focusedTaskIds: [],
    },
    // Filters
    filters: {},
  },
  [VIEW_TYPES.TABLE]: {
    // FOKUS mode
    fokusMode: {
      isActive: false,
      focusedTaskIds: [],
    },
    // Sort
    sortSetting: 'manual',
    // Filters
    filters: {},
  },
  [VIEW_TYPES.GANTT]: {
    // FOKUS mode
    fokusMode: {
      isActive: false,
      focusedTaskIds: [],
    },
    // Viewport state (zoom level, scroll - always centers on NOW)
    viewport: {
      zoomLevel: 'week', // day, week, month, quarter
      scrollPosition: null, // null = center on NOW
    },
    // Swimlane grouping (same as Kanban - off by default)
    ganttSwimlane: 'none', // none, status, priority, tag, difficulty
    // Sort setting (same as Kanban - manual by default)
    sortSetting: 'manual',
    // Filters
    filters: {},
  },
  [VIEW_TYPES.TODOLIST]: {
    // FOKUS mode
    fokusMode: {
      isActive: false,
      focusedTaskIds: [],
    },
    // View mode (master/simple/swimlane)
    viewMode: 'master',
    // Swimlane grouping (when in swimlane mode)
    swimlaneGroupBy: 'status',
    // Sort
    sortSetting: 'manual',
    // Filters
    filters: {},
  },
  [VIEW_TYPES.NEURAL]: {
    filters: {},
  },
  [VIEW_TYPES.BOARD]: {
    filters: {},
  },
};

// ═══════════════════════════════════════════════════════════════
// HOOKS SUPPORT
// ═══════════════════════════════════════════════════════════════

/**
 * Get all view types as options for a dropdown
 * @returns {Array<{value: string, label: string}>}
 */
export function getViewTypeOptions() {
  return Object.values(VIEW_TYPES).map(type => ({
    value: type,
    label: VIEW_TYPE_LABELS[type],
  }));
}
